import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertTriangle, Download, Shield, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useAccountDeletion } from '@/hooks/useAccountDeletion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DeleteAccountModal = ({ isOpen, onClose }: DeleteAccountModalProps) => {
  const [step, setStep] = useState<'warning' | 'export' | 'confirm' | 'type'>('warning');
  const [deletionType, setDeletionType] = useState<'complete' | 'anonymize'>('complete');
  const [confirmationEmail, setConfirmationEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmationText, setConfirmationText] = useState('');
  const [hasExported, setHasExported] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  const { user } = useAuth();
  const { t } = useTranslation('profile');
  const { isDeleting, isExporting, exportUserData, deleteAccount, anonymizeAccount } = useAccountDeletion();

  const handleExport = async () => {
    try {
      await exportUserData();
      setHasExported(true);
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  const handleDelete = async () => {
    try {
      if (deletionType === 'complete') {
        await deleteAccount(confirmationEmail, password);
      } else {
        await anonymizeAccount(confirmationEmail, password);
      }
      onClose();
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  const resetModal = () => {
    setStep('warning');
    setDeletionType('complete');
    setConfirmationEmail('');
    setPassword('');
    setConfirmationText('');
    setHasExported(false);
    setAgreedToTerms(false);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const isConfirmationValid = () => {
    return confirmationEmail === user?.email && 
           password.length > 0 && 
           confirmationText === 'DELETE MY ACCOUNT' &&
           agreedToTerms;
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" />
            {t('account.deleteTitle', 'Delete Account')}
          </DialogTitle>
          <DialogDescription>
            {t('account.deleteDescription', 'Permanently delete your uplivion account and all associated data')}
          </DialogDescription>
        </DialogHeader>

        {step === 'warning' && (
          <div className="space-y-4">
            <Card className="border-red-200 bg-red-50">
              <CardHeader>
                <CardTitle className="text-red-800 text-lg">
                  {t('account.warningTitle', 'This action cannot be undone')}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-red-700 space-y-2">
                <p>{t('account.warningText1', 'Deleting your account will permanently remove:')}</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>{t('account.warningItem1', 'Your profile and personal information')}</li>
                  <li>{t('account.warningItem2', 'All timeline entries you created')}</li>
                  <li>{t('account.warningItem3', 'All managed profiles you own')}</li>
                  <li>{t('account.warningItem4', 'Your friendships and connections')}</li>
                  <li>{t('account.warningItem5', 'All stored photos and media')}</li>
                </ul>
              </CardContent>
            </Card>

            <div className="flex justify-between">
              <Button variant="outline" onClick={handleClose}>
                {t('account.cancel', 'Cancel')}
              </Button>
              <Button 
                variant="destructive" 
                onClick={() => setStep('export')}
                className="gap-2"
              >
                {t('account.continue', 'Continue')}
              </Button>
            </div>
          </div>
        )}

        {step === 'export' && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="w-5 h-5" />
                  {t('account.exportTitle', 'Export Your Data')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  {t('account.exportDescription', 'Before deleting your account, you can export your data. This includes your timeline entries, profile information, and managed profiles.')}
                </p>
                
                <Button 
                  onClick={handleExport}
                  disabled={isExporting}
                  className="gap-2"
                >
                  <Download className="w-4 h-4" />
                  {isExporting ? t('account.exporting', 'Exporting...') : t('account.exportData', 'Export My Data')}
                </Button>

                {hasExported && (
                  <p className="text-green-600 text-sm">
                    {t('account.exportComplete', 'Data exported successfully!')}
                  </p>
                )}
              </CardContent>
            </Card>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep('warning')}>
                {t('account.back', 'Back')}
              </Button>
              <Button 
                variant="destructive" 
                onClick={() => setStep('type')}
              >
                {t('account.continue', 'Continue')}
              </Button>
            </div>
          </div>
        )}

        {step === 'type' && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  {t('account.typeTitle', 'Choose Deletion Type')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div 
                    className={`p-4 rounded-lg border cursor-pointer transition-all ${
                      deletionType === 'complete' ? 'border-red-500 bg-red-50' : 'border-gray-200'
                    }`}
                    onClick={() => setDeletionType('complete')}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        checked={deletionType === 'complete'}
                        onChange={() => setDeletionType('complete')}
                        className="mt-1"
                      />
                      <div>
                        <h4 className="font-medium text-red-700">
                          {t('account.completeDelete', 'Complete Deletion')}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {t('account.completeDeleteDesc', 'Permanently remove all data. This cannot be undone.')}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div 
                    className={`p-4 rounded-lg border cursor-pointer transition-all ${
                      deletionType === 'anonymize' ? 'border-orange-500 bg-orange-50' : 'border-gray-200'
                    }`}
                    onClick={() => setDeletionType('anonymize')}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        checked={deletionType === 'anonymize'}
                        onChange={() => setDeletionType('anonymize')}
                        className="mt-1"
                      />
                      <div>
                        <h4 className="font-medium text-orange-700">
                          {t('account.anonymize', 'Anonymize Data')}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {t('account.anonymizeDesc', 'Remove personal information but keep timeline structure for friends.')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep('export')}>
                {t('account.back', 'Back')}
              </Button>
              <Button 
                variant="destructive" 
                onClick={() => setStep('confirm')}
              >
                {t('account.continue', 'Continue')}
              </Button>
            </div>
          </div>
        )}

        {step === 'confirm' && (
          <div className="space-y-4">
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="text-red-700">
                  {t('account.finalConfirmation', 'Final Confirmation')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="confirm-email">
                      {t('account.confirmEmail', 'Confirm your email address')}
                    </Label>
                    <Input
                      id="confirm-email"
                      type="email"
                      placeholder={user?.email}
                      value={confirmationEmail}
                      onChange={(e) => setConfirmationEmail(e.target.value)}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="password">
                      {t('account.enterPassword', 'Enter your password')}
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="confirm-text">
                      {t('account.typeConfirmation', 'Type "DELETE MY ACCOUNT" to confirm')}
                    </Label>
                    <Input
                      id="confirm-text"
                      type="text"
                      placeholder="DELETE MY ACCOUNT"
                      value={confirmationText}
                      onChange={(e) => setConfirmationText(e.target.value)}
                      className="mt-1"
                    />
                  </div>

                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="agree-terms"
                      checked={agreedToTerms}
                      onCheckedChange={(checked) => setAgreedToTerms(checked as boolean)}
                    />
                    <Label htmlFor="agree-terms" className="text-sm">
                      {t('account.agreeTerms', 'I understand that this action is permanent and cannot be undone')}
                    </Label>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep('type')}>
                {t('account.back', 'Back')}
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDelete}
                disabled={!isConfirmationValid() || isDeleting}
                className="gap-2"
              >
                <Trash2 className="w-4 h-4" />
                {isDeleting 
                  ? t('account.deleting', 'Deleting...') 
                  : deletionType === 'complete'
                    ? t('account.deleteAccount', 'Delete Account')
                    : t('account.anonymizeAccount', 'Anonymize Account')
                }
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};